import type { AdminLlmUsage } from './api'

/**
 * The cost line (agent-1.1 S2): turns, tokens and money, never content. Tokens lead
 * the money (owner, 2026-10-12): they are what the model was asked to do, and are
 * known even while a price is not. USD as priced (owner ruling K16). A window with any
 * unpriced call says so rather than showing a partial sum, and a read that failed says
 * so rather than "0 turns".
 */
export function usageLine(usage: { value: AdminLlmUsage | null; failed: boolean }): string {
  if (usage.failed || usage.value === null) return usage.failed ? 'Could not be read' : '—'
  const { turns, cost, unpriced_calls: unpriced } = usage.value
  const surfaces = usage.value.by_surface ?? []
  const input = surfaces.reduce((sum, s) => sum + s.input_tokens, 0)
  const output = surfaces.reduce((sum, s) => sum + s.output_tokens, 0)
  const count = `${turns} ${turns === 1 ? 'turn' : 'turns'}, ${formatTokens(input)} tokens in, ${formatTokens(output)} out`
  if (unpriced > 0 || cost === null) return `${count}, cost not yet priced`
  // Final review: calls that reached Bedrock but reported no tokens (a timeout) may be
  // billed and cannot be priced, so the sum is a floor, never "the" cost.
  const unmetered = usage.value.unmetered_calls ?? 0
  if (unmetered > 0) return `${count}, at least ${formatUsd(Number(cost))} (${unmetered} not metered)`
  return `${count}, ${formatUsd(Number(cost))}`
}

/** Dollars, to the cent from $1, and to four places below it (a turn costs fractions of a cent). */
function formatUsd(amount: number): string {
  return `$${amount >= 1 ? amount.toFixed(2) : amount.toFixed(4)}`
}

/** Tokens as a person reads them: 400, 3.5k, 1.2M. */
function formatTokens(count: number): string {
  if (count < 1000) return String(count)
  if (count < 1_000_000) return `${trim(count / 1000)}k`
  return `${trim(count / 1_000_000)}M`
}

function trim(value: number): string {
  return value.toFixed(1).replace(/\.0$/, '')
}
